import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isSignInWithEmailLink, signInWithEmailLink, applyActionCode } from "firebase/auth";
import { auth, db } from "../../lib/firebase";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { CheckCircle, XCircle, Loader } from "lucide-react";

export default function EmailVerify() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const verifyEmail = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      const emailParam = searchParams.get('email');

      // Check if this is a Firebase Email Verification link
      if (mode === 'verifyEmail' && oobCode) {
        console.log('[EmailVerify] Firebase Email Verification detected');
        try {
          // Apply the verification code
          await applyActionCode(auth, oobCode);
          console.log('[EmailVerify] ✅ Email verified successfully');

          // Get current user or wait for auth state
          const currentUser = auth.currentUser;
          
          if (currentUser) {
            // Reload user to get updated emailVerified status
            await currentUser.reload();
            
            // Update user document
            const userRef = doc(db, "users", currentUser.uid);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
              await updateDoc(userRef, {
                emailVerified: true,
                updatedAt: new Date()
              });
              
              const signupSource = userDoc.data()?.signupSource;
              console.log('[EmailVerify] Signup source:', signupSource);

              setStatus('success');
              setMessage("ยืนยันอีเมลสำเร็จ! กำลังนำคุณกลับไปหน้าเดิม...");

              // Redirect based on signup source after 2 seconds
              setTimeout(() => {
                if (signupSource === 'coursedee') {
                  window.location.href = 'https://aiforyou.web.app/';
                } else if (signupSource === 'salepage') {
                  window.location.href = 'https://saledi.web.app/';
                } else {
                  navigate('/');
                }
              }, 2000);
            } else {
              setStatus('success');
              setMessage("ยืนยันอีเมลสำเร็จ! กำลังนำคุณกลับไปหน้าเดิม...");
              setTimeout(() => navigate('/'), 2000);
            }
          } else {
            // User not logged in, show success and redirect to login
            setStatus('success');
            setMessage("ยืนยันอีเมลสำเร็จ! กรุณาเข้าสู่ระบบเพื่อเริ่มใช้งาน");
            
            // Redirect to login page
            setTimeout(() => {
              const hostname = window.location.hostname;
              if (hostname.includes('aiforyou')) {
                window.location.href = 'https://aiforyou.web.app/login';
              } else if (hostname.includes('salepage')) {
                window.location.href = 'https://saledi.web.app/login';
              } else {
                navigate('/login');
              }
            }, 2000);
          }
        } catch (err: any) {
          console.error('[EmailVerify] Email verification error:', err);
          setStatus('error');
          
          if (err.code === 'auth/invalid-action-code') {
            setMessage("ลิงก์หมดอายุหรือถูกใช้งานแล้ว กรุณาขอลิงก์ใหม่");
          } else if (err.code === 'auth/expired-action-code') {
            setMessage("ลิงก์หมดอายุแล้ว กรุณาขอลิงก์ใหม่");
          } else {
            setMessage("เกิดข้อผิดพลาด: " + (err.message || "กรุณาลองใหม่อีกครั้ง"));
          }
        }
        return;
      }

      // Check if this is an Email Link Sign-in (legacy)
      if (isSignInWithEmailLink(auth, window.location.href)) {
        console.log('[EmailVerify] Email Link Sign-in detected (legacy)');
        try {
          // Get email from localStorage or URL param
          let email = window.localStorage.getItem('emailForSignIn') || emailParam;

          // If email is not available, ask user to provide it
          if (!email) {
            email = window.prompt('กรุณากรอกอีเมลของคุณเพื่อยืนยัน');
          }

          if (!email) {
            setStatus('error');
            setMessage("ไม่พบอีเมล กรุณาลองใหม่อีกครั้ง");
            return;
          }

          // Sign in with email link
          const result = await signInWithEmailLink(auth, email, window.location.href);
          
          // Clear email from localStorage
          window.localStorage.removeItem('emailForSignIn');

          // Check if user document exists
          const userRef = doc(db, "users", result.user.uid);
          const userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            // New user - create profile
            await setDoc(userRef, {
              uid: result.user.uid,
              email: result.user.email,
              name: result.user.email?.split("@")[0] || "",
              displayName: result.user.email?.split("@")[0] || "",
              role: "instructor",
              purchasedCourses: [],
              createdAt: new Date(),
            });
          }

          // Get signup source to determine redirect
          const signupSource = userDoc.exists() ? userDoc.data()?.signupSource : null;
          console.log('[EmailVerify] Signup source:', signupSource);

          setStatus('success');
          setMessage("เข้าสู่ระบบสำเร็จ! กำลังนำคุณกลับไปหน้าเดิม...");

          // Redirect based on signup source after 2 seconds
          setTimeout(() => {
            if (signupSource === 'coursedee') {
              window.location.href = 'https://aiforyou.web.app/';
            } else if (signupSource === 'salepage') {
              window.location.href = 'https://saledi.web.app/';
            } else {
              navigate('/');
            }
          }, 2000);

        } catch (err: any) {
          console.error('[EmailVerify] Email link sign-in error:', err);
          setStatus('error');
          
          if (err.code === 'auth/invalid-action-code') {
            setMessage("ลิงก์หมดอายุหรือถูกใช้งานแล้ว กรุณาขอลิงก์ใหม่");
          } else if (err.code === 'auth/expired-action-code') {
            setMessage("ลิงก์หมดอายุแล้ว กรุณาขอลิงก์ใหม่");
          } else {
            setMessage("เกิดข้อผิดพลาด: " + (err.message || "กรุณาลองใหม่อีกครั้ง"));
          }
        }
        return;
      }

      // If neither verification type, show error
      setStatus('error');
      setMessage("ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว");
    };

    verifyEmail();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-blue-100">
              <Loader className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">กำลังยืนยันอีเมล...</h2>
            <p className="text-gray-600">กรุณารอสักครู่</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-100">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">ยืนยันอีเมลสำเร็จ!</h2>
            <p className="text-gray-600">{message}</p>
            <div className="mt-6">
              <div className="animate-pulse flex justify-center">
                <div className="h-2 w-32 bg-blue-200 rounded"></div>
              </div>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-red-100">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">เกิดข้อผิดพลาด</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              กลับสู่หน้าหลัก
            </button>
          </>
        )}
      </div>
    </div>
  );
}
